import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../services/grafikService';
import { API_BASE } from '../../config/environment';

/**
 * Karta "Dodaj grafik do kalendarza" (subskrypcja .ics).
 * Pokazuje prywatny URL feedu + linki webcal (iPhone) / Google Calendar + instrukcje.
 * Dla admina dodatkowo feed "wszyscy pracownicy".
 */

// Baza absolutna do zbudowania pelnego URL (webcal/Google wymagaja absolutnego).
const absBase = () => API_BASE || window.location.origin;

const fullUrl = (path) => `${absBase()}${path}`;
const webcalUrl = (path) => `${absBase()}${path}`.replace(/^https?:\/\//i, 'webcal://');
const googleUrl = (path) =>
  `https://calendar.google.com/calendar/u/0/r/settings/addbyurl?cid=${encodeURIComponent(fullUrl(path))}`;

const CalendarSubscribeCard = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feeds, setFeeds] = useState(null); // { wlasny:{path,opis}, wszyscy?:{...} }
  const [activeFeed, setActiveFeed] = useState('wlasny');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await grafikService.getCalendarUrl();
      const data = res?.data || res;
      setFeeds(data || null);
    } catch (err) {
      toast.error(err.message || 'Nie udalo sie pobrac adresu kalendarza');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !feeds) load();
  };

  const handleCopy = async (path) => {
    try {
      await navigator.clipboard.writeText(fullUrl(path));
      toast.success('Skopiowano adres kalendarza');
    } catch {
      toast.error('Nie udalo sie skopiowac - zaznacz i skopiuj recznie');
    }
  };

  const handleReset = async (typ) => {
    if (!window.confirm(
      'Wygenerowac NOWY adres? Stary przestanie dzialac - trzeba bedzie ' +
      'ponownie dodac kalendarz na kazdym urzadzeniu.'
    )) return;
    setLoading(true);
    try {
      const res = await grafikService.resetCalendarUrl(typ);
      const data = res?.data || res;
      // Podmien sciezke dla danego feedu
      setFeeds(prev => ({
        ...prev,
        [typ]: { ...(prev?.[typ] || {}), path: data?.path }
      }));
      toast.success('Wygenerowano nowy adres');
    } catch (err) {
      toast.error(err.message || 'Blad resetu adresu');
    } finally {
      setLoading(false);
    }
  };

  const feed = feeds?.[activeFeed];
  const hasAdminFeed = !!feeds?.wszyscy;

  const linkBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)', cursor: 'pointer',
    fontSize: '12px', fontWeight: 600, textDecoration: 'none'
  };

  return (
    <div style={{
      marginTop: '1rem',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '6px',
      overflow: 'hidden'
    }}>
      <button
        onClick={toggle}
        style={{
          width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.85rem 1.1rem',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--text)', fontSize: '0.9rem', fontWeight: 600
        }}>
        <span>
          <i className="fas fa-calendar-plus" style={{ marginRight: '0.5rem', color: 'var(--info)' }} />
          Dodaj grafik do kalendarza (Google / iPhone)
        </span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ color: 'var(--text-secondary)' }} />
      </button>

      {open && (
        <div style={{ padding: '0 1.1rem 1.1rem', borderTop: '1px solid var(--border)' }}>
          {loading && !feed && (
            <div style={{ padding: '1rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }} />
              Ladowanie adresu...
            </div>
          )}

          {/* Przelacznik feedow (admin) */}
          {hasAdminFeed && (
            <div style={{ display: 'flex', gap: '0.4rem', margin: '0.85rem 0' }}>
              {['wlasny', 'wszyscy'].map(t => (
                <button key={t} onClick={() => setActiveFeed(t)}
                  style={{
                    padding: '6px 12px', cursor: 'pointer',
                    border: '1px solid var(--border)', fontSize: '12px', fontWeight: 600,
                    backgroundColor: activeFeed === t ? 'var(--info)' : 'var(--card-bg)',
                    color: activeFeed === t ? '#fff' : 'var(--text-secondary)'
                  }}>
                  {t === 'wlasny' ? 'Moj grafik' : 'Wszyscy pracownicy'}
                </button>
              ))}
            </div>
          )}

          {feed && (
            <>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0.85rem 0 0.5rem' }}>
                Skopiuj ten prywatny adres i dodaj jako <strong>subskrybowany kalendarz</strong>.
                Grafik bedzie sam sie aktualizowal (Google odswieza co kilka-kilkanascie godzin).
              </p>

              {/* Pole z URL + kopiuj */}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  readOnly
                  value={fullUrl(feed.path)}
                  onFocus={(e) => e.target.select()}
                  style={{
                    flex: 1, minWidth: '220px',
                    padding: '0.55rem 0.7rem', borderRadius: '4px',
                    border: '1px solid var(--border)', backgroundColor: 'var(--bg)',
                    color: 'var(--text)', fontSize: '0.8rem', fontFamily: 'monospace'
                  }}
                />
                <button onClick={() => handleCopy(feed.path)}
                  style={{ ...linkBtnStyle, backgroundColor: 'var(--info)', color: '#fff', border: 'none' }}>
                  <i className="fas fa-copy" /> Kopiuj
                </button>
              </div>

              {/* Szybkie linki */}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem', flexWrap: 'wrap' }}>
                <a href={webcalUrl(feed.path)} style={linkBtnStyle}>
                  <i className="fab fa-apple" /> Dodaj na iPhone
                </a>
                <a href={googleUrl(feed.path)} target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>
                  <i className="fab fa-google" /> Dodaj do Google
                </a>
              </div>

              {/* Instrukcje */}
              <details style={{ marginTop: '0.9rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Jak dodac recznie?</summary>
                <div style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
                  <strong>iPhone / iPad:</strong> Ustawienia &rarr; Kalendarz &rarr; Konta &rarr;
                  Dodaj konto &rarr; Inne &rarr; <em>Dodaj subskrybowany kalendarz</em> &rarr; wklej adres.<br />
                  <strong>Google Calendar (komputer):</strong> Inne kalendarze (po lewej) &rarr; <em>+</em> &rarr;
                  <em>Z adresu URL</em> &rarr; wklej adres &rarr; Dodaj kalendarz.<br />
                  <strong>Android:</strong> dodaj w Google Calendar na komputerze (jak wyzej) -
                  zsynchronizuje sie na telefonie.
                </div>
              </details>

              {/* Reset tokenu */}
              <div style={{ marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: '1px dashed var(--border)' }}>
                <button onClick={() => handleReset(activeFeed)} disabled={loading}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--danger)', fontSize: '0.78rem', fontWeight: 600, padding: 0
                  }}>
                  <i className="fas fa-rotate" style={{ marginRight: '0.35rem' }} />
                  Wygeneruj nowy adres (uniewaznij stary)
                </button>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                  Uzyj, jesli adres wyciekl. Stary feed przestanie dzialac.
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarSubscribeCard;
